import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Table,
  Truncate,
  Typography,
} from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import { REACT_APP_AUTH_ROLE_OPERATOR } from '../../../AuthProvider';
import { videosAwaitingApprovalAtom } from '../../../atoms/videoListings';
import AwaitingApprovalActionMessages from '../../../components/AwaitingApprovalActionMessages';
import CellHeadings from '../../../components/CellHeadings';
import Comments from '../../../components/Comments';
import MatchInfo from '../../../components/MatchInfo';
import QAStatus from '../../../components/QAStatus';
import Secured from '../../../components/Secured';
import DeleteVideo from '../../../components/VideoActions/DeleteVideo';
import EditMetadata from '../../../components/VideoActions/EditMetadata';
import { MoveVideo } from '../../../components/VideoActions/MoveVideo/MoveVideo';
import RejectVideo from '../../../components/VideoActions/RejectVideo';
import VideoInfo from '../../../components/VideoInfo';
import { sourceDeserialiser } from '../../../serialisers/sourceData';
import { videoRefreshInProgressAtom } from '../../../atoms/polling';

export function UploadsDisplay({ title, onRefresh }) {
  const intl = useIntl();

  const videoRefreshInProgress = useAtomValue(videoRefreshInProgressAtom);

  const videosAwaitingApproval = useAtomValue(videosAwaitingApprovalAtom);

  const theme = useTheme();
  const isSmallScreen = !theme.mqMatch(theme.mqFrom('lg'));

  const hasComments =
    videosAwaitingApproval.filter((video) => video.Comments).length > 0;

  return (
    <Card>
      <CardHeader>
        <Typography variant="headingLarge">
          {title || (
            <FormattedMessage
              defaultMessage="Videos Awaiting Approval"
              id="a5190"
            />
          )}
        </Typography>
        <Button onClick={onRefresh} isPending={videoRefreshInProgress}>
          <FormattedMessage defaultMessage="Refresh" id="63a6a" />
        </Button>
      </CardHeader>
      <CardBody>
        <AwaitingApprovalActionMessages />
        {isSmallScreen ? (
          <Table>
            {videosAwaitingApproval?.length > 0 && (
              <CellHeadings
                cells={[
                  {
                    children: intl.formatMessage({
                      defaultMessage: 'Match Info',
                      id: '9ca52',
                    }),
                    id: 'Match',
                  },
                  {
                    children: intl.formatMessage({
                      defaultMessage: 'Video Info',
                      id: '28f69',
                    }),
                    id: 'Video',
                  },
                  {
                    children: intl.formatMessage({
                      defaultMessage: 'Actions',
                      id: '06df3',
                    }),
                    id: 'Actions',
                  },
                ]}
                textAlign="left"
              />
            )}
            <Table.Body>
              {videosAwaitingApproval.length > 0 ? (
                videosAwaitingApproval?.map((listing) => (
                  <Table.Row key={`table-row-${listing.key}`}>
                    <Table.DataCell $textAlign="left">
                      <MatchInfo listing={listing} />
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      <VideoInfo listing={listing} />
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      <ButtonGroup>
                        <Secured allowed={REACT_APP_AUTH_ROLE_OPERATOR}>
                          <EditMetadata
                            videoKey={listing.key}
                            matchId={listing.matchId}
                            matchPart={listing.matchPart}
                            track={listing.track}
                            version={listing.version}
                            sport={listing.sport}
                            size="small"
                          />
                        </Secured>
                        <DeleteVideo
                          qaStatus={listing.qAStatus}
                          size="small"
                          videoKey={listing.key}
                        />
                        <Secured allowed={REACT_APP_AUTH_ROLE_OPERATOR}>
                          <RejectVideo
                            currentStatus={listing.qAStatus}
                            size="small"
                            videoKey={listing.key}
                          />
                        </Secured>
                        <Secured allowed={REACT_APP_AUTH_ROLE_OPERATOR}>
                          <MoveVideo
                            matchId={listing.matchId}
                            matchVideoAlreadyExists={
                              listing.matchVideoAlreadyExists
                            }
                            part={listing.matchPart}
                            qualityAssuranceInfo={listing.qualityAssuranceInfo}
                            url={listing.presignedUrl}
                            size="small"
                            sourceData={{
                              ...sourceDeserialiser(listing.source_data),
                            }}
                            sport={listing.sport}
                            track={listing.track}
                            uploadedByEmail={
                              listing.qualityAssuranceInfo?.uploadedBy
                                ?.emailAddress
                            }
                            uploadedById={
                              listing.qualityAssuranceInfo?.uploadedBy?.userId
                            }
                            version={listing.version}
                            videoKey={listing.key}
                          />
                        </Secured>
                      </ButtonGroup>
                    </Table.DataCell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row key="table-row-empty-listing">
                  <Table.DataCell>
                    <FormattedMessage
                      defaultMessage="Videos Will Appear Here"
                      id="c3e6f"
                    />
                  </Table.DataCell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        ) : (
          <Table stickyColumns={0}>
            <CellHeadings
              cells={[
                {
                  children: intl.formatMessage({
                    defaultMessage: 'Match Info',
                    id: '9ca52',
                  }),
                  id: 'MatchId',
                },
                {
                  children: intl.formatMessage({
                    defaultMessage: 'Video Info',
                    id: '28f69',
                  }),
                  id: 'Video',
                },
                {
                  children: intl.formatMessage({
                    defaultMessage: 'Filename',
                    id: '13510',
                  }),
                  id: 'Filename',
                },
                {
                  children: intl.formatMessage({
                    defaultMessage: 'Size',
                    id: '6f6cb',
                  }),
                  id: 'Size',
                },
                {
                  children: intl.formatMessage({
                    defaultMessage: 'Status',
                    id: 'ec53a',
                  }),
                  id: 'QA Status',
                },
                hasComments
                  ? {
                      children: intl.formatMessage({
                        defaultMessage: 'Comments',
                        id: '8413c',
                      }),
                      id: 'Comments',
                    }
                  : {},
                {
                  children: intl.formatMessage({
                    defaultMessage: 'Actions',
                    id: '06df3',
                  }),
                  id: 'Actions',
                },
                {
                  children: intl.formatMessage({
                    defaultMessage: 'Modified',
                    id: '35e0c',
                  }),
                  id: 'Modified',
                },
              ]}
              textAlign="left"
            />
            <Table.Body>
              {videosAwaitingApproval.length > 0 ? (
                videosAwaitingApproval?.map((listing) => (
                  <Table.Row key={`table-row-${listing.key}`}>
                    <Table.DataCell $textAlign="left">
                      <MatchInfo listing={listing} />
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      <VideoInfo listing={listing} />
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      {listing.key && <Truncate>{listing.key}</Truncate>}
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      {(listing.contentLength / 1000000000).toFixed(2)}GB
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      <QAStatus qaStatus={listing.qAStatus} />
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      {listing.comments && (
                        <Comments
                          comments={listing.comments}
                          videoKey={listing.key}
                        />
                      )}
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      <ButtonGroup>
                        <Secured allowed={REACT_APP_AUTH_ROLE_OPERATOR}>
                          <EditMetadata
                            videoKey={listing.key}
                            matchId={listing.matchId}
                            matchPart={listing.matchPart}
                            track={listing.track}
                            version={listing.version}
                            sport={listing.sport}
                            size="small"
                          />
                        </Secured>
                        <DeleteVideo
                          qaStatus={listing.qAStatus}
                          size="small"
                          videoKey={listing.key}
                        />
                        <Secured allowed={REACT_APP_AUTH_ROLE_OPERATOR}>
                          <RejectVideo
                            currentStatus={listing.qAStatus}
                            size="small"
                            videoKey={listing.key}
                          />
                        </Secured>
                        <Secured allowed={REACT_APP_AUTH_ROLE_OPERATOR}>
                          <MoveVideo
                            matchId={listing.matchId}
                            matchVideoAlreadyExists={
                              listing.matchVideoAlreadyExists
                            }
                            part={listing.matchPart}
                            qualityAssuranceInfo={listing.qualityAssuranceInfo}
                            url={listing.presignedUrl}
                            size="small"
                            sourceData={{
                              ...sourceDeserialiser(listing.source_data),
                            }}
                            sport={listing.sport}
                            track={listing.track}
                            uploadedByEmail={
                              listing.qualityAssuranceInfo?.uploadedBy
                                ?.emailAddress
                            }
                            uploadedById={
                              listing.qualityAssuranceInfo?.uploadedBy?.userId
                            }
                            version={listing.version}
                            videoKey={listing.key}
                          />
                        </Secured>
                      </ButtonGroup>
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      {moment(listing.lastModified).fromNow()}
                    </Table.DataCell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row key="table-row-empty-listing">
                  <Table.DataCell $textAlign="left" colSpan={10}>
                    <FormattedMessage
                      defaultMessage="Your Videos Will Appear Here"
                      id="9fb2f"
                    />
                  </Table.DataCell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

UploadsDisplay.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  title: PropTypes.string,
};
UploadsDisplay.defaultProps = {
  title: undefined,
};

export default { UploadsDisplay };
